//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React from 'react';

/**
 * Tippy requires the top level element for its target to be a DOM node so that it can
 * properly set up the refs and event handlers for the underlying Popper instance. This
 * simple HOC validates whether the target element is a DOM node, and follows the Tippy
 * suggestion of wrapping the target in a <span> if it is not.
 *
 * https://github.com/atomiks/tippyjs-react#component-children
 *
 * There is no styling attached to the span element because any custom styling should
 * be applied on the consumer side, even if it requires an extra DOM node wrapper.
 *
 * @param target The target element for the Popover. This type is copied from Tippy.
 */
export function asPopoverTarget(target: React.ReactElement) {
  /**
   * Per this article, we can safely assume that any target with a string type is already a DOM node.
   * https://reactjs.org/blog/2015/12/18/react-components-elements-and-instances.html#dom-elements
   */
  const isDirectDOMNode = typeof target.type === 'string';

  /**
   * Per this article, the type attr of a ReactElement will either be a string or a `ReactClass`. If the class's
   * target is a string, then it is a forwarded ref to a DOM node and will also work as a Popover target.
   *
   * https://reactjs.org/blog/2015/12/18/react-components-elements-and-instances.html#elements-describe-the-tree
   */
  const isForwardedDOMNode =
    typeof (target.type as unknown as { target: unknown }).target === 'string';

  return isDirectDOMNode || isForwardedDOMNode ? target : <span>{target}</span>;
}
